import { Link } from "react-router-dom";
import useSeo from "@/lib/useSeo";

const CONTACT_EMAIL = "Gallenhunt90@gmail.com";
const BUSINESS_NAME = "The Couponer";
const BUSINESS_LOCATION = "the United States";

export default function Terms() {
  useSeo({
    title: "Terms of Service · The Couponer",
    description: "The rules of the road for using The Couponer — accounts, payments, sponsored deals, and more.",
    canonical: "https://thecouponer.com/terms",
  });

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12" data-testid="terms-page">
      <div className="eyebrow text-[10px] text-[#5c5c5c] mb-2">LEGAL</div>
      <h1 className="serif text-5xl md:text-6xl leading-tight tracking-tight mb-6">Terms of Service</h1>
      <p className="text-sm text-[#5c5c5c] mb-8">
        Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
      </p>

      <div className="max-w-none space-y-6 text-[15px] leading-relaxed">
        <p>
          Welcome to {BUSINESS_NAME}. By using thecouponer.com (the &quot;Service&quot;), you agree to these Terms. If you don&#39;t agree, please don&#39;t use the Service. These Terms are governed by the laws of {BUSINESS_LOCATION}.
        </p>

        <h2 className="serif text-2xl mt-8">1. Accounts</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>You must be at least 13 years old.</li>
          <li>You&#39;re responsible for keeping your password safe. Notify us at <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a> if your account is compromised.</li>
          <li>One account per person. Don&#39;t impersonate anyone.</li>
        </ul>

        <h2 className="serif text-2xl mt-8">2. What we do (and don&#39;t)</h2>
        <p>
          The Service aggregates publicly-available coupon codes, sale links, and community-submitted deals from third-party retailers. We are <strong>not</strong> the seller. When you click a deal, you leave our site and transact directly with the retailer. All prices, availability, and codes are subject to the retailer&#39;s own terms.
        </p>
        <p>
          Codes expire, get pulled, or stop working. We do our best to verify but make no guarantee. If a code fails, try another one from the same store — chances are one still works.
        </p>

        <h2 className="serif text-2xl mt-8">3. Affiliate links</h2>
        <p>
          Some outbound links include affiliate tracking (Skimlinks, Amazon Associates). When you buy through those links, we earn a small commission at no extra cost to you. This helps keep the Service free. We only add affiliate parameters &mdash; we never change the retailer, price, or discount you see.
        </p>

        <h2 className="serif text-2xl mt-8">4. Community submissions</h2>
        <p>
          Users can submit deals via the SUBMIT page. By submitting, you grant us a worldwide, royalty-free license to display, edit, and remove your submission. You warrant that the submission is accurate and doesn&#39;t violate anyone else&#39;s rights. We reserve the right to remove any submission at our discretion.
        </p>

        <h2 className="serif text-2xl mt-8">5. Pro subscriptions</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Pro is $4.99/month, billed via Stripe.</li>
          <li>Cancel anytime — access continues through the end of the current billing period.</li>
          <li>Refunds are handled case-by-case within 7 days of purchase. Email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.</li>
        </ul>

        <h2 className="serif text-2xl mt-8">6. Sponsored deals (retailers)</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Bronze/Silver/Gold tiers listed on <Link to="/sponsor" className="underline">/sponsor</Link>. Payment is upfront and non-refundable once the deal is live.</li>
          <li>Sponsored deals must be legal, active offers from the retailer whose name appears on the coupon. No misleading claims.</li>
          <li>We reserve the right to remove any sponsored deal that violates these Terms, without refund.</li>
          <li>The gold &quot;SPONSORED&quot; ribbon appears on all paid placements — we won&#39;t hide the fact that a deal was paid to be there.</li>
        </ul>

        <h2 className="serif text-2xl mt-8">7. Acceptable use</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Don&#39;t scrape or copy the Service in bulk without written permission.</li>
          <li>Don&#39;t submit spam, fake, or misleading deals.</li>
          <li>Don&#39;t attempt to hack, break, or overload the Service.</li>
          <li>Don&#39;t use the Service for anything illegal.</li>
        </ul>

        <h2 className="serif text-2xl mt-8">8. Disclaimers</h2>
        <p>
          The Service is provided <strong>&quot;as is&quot;</strong>, without warranties of any kind. We are not liable for expired codes, out-of-stock products, retailer errors, shipping issues, or anything else that happens between you and the retailer.
        </p>

        <h2 className="serif text-2xl mt-8">9. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, our total liability to you for any claim arising from your use of the Service is limited to the amount you paid us in the preceding 12 months (or $50 if you paid nothing).
        </p>

        <h2 className="serif text-2xl mt-8">10. Termination</h2>
        <p>
          You can close your account anytime by emailing <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>. We may suspend or terminate accounts that violate these Terms, without notice.
        </p>

        <h2 className="serif text-2xl mt-8">11. Changes</h2>
        <p>We may update these Terms at any time. Material changes will be announced via email or a banner on the site. Continued use after changes = acceptance.</p>

        <h2 className="serif text-2xl mt-8">12. Contact</h2>
        <p>
          {BUSINESS_NAME} · Based in {BUSINESS_LOCATION}<br />
          Questions? <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
        </p>

        <p className="text-sm text-[#5c5c5c] mt-10">
          See also: <Link to="/privacy" className="underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
